// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierCamera.js   ·   THE SHARED CAMERA STATE
// ────────────────────────────────────────────────────────────────────
// A dependency-free module. It imports NOTHING — not three, not r3f,
// not React. That is deliberate and it is the law:
//
//   ### GAME LOGIC MUST NOT IMPORT A RENDERER TO LEARN WHICH WAY IS
//   ### FORWARD.  logic → data ← renderer.
//
// The movement loop in UnstablePierre.js reads `groundForward()`. The
// 3D rig writes it. Neither knows the other exists.
//
// ── ⭐ THE DEFAULT IS THE IDENTITY ──────────────────────────────────
// The forward starts at (0, -1) — screen-up, tile-north — which is
// EXACTLY what the Pier assumed before this module existed. So the
// rotation can be applied UNCONDITIONALLY, with zero risk to the 2D
// view, which never touches a camera at all.
//
//   A cross-cutting transform is only safe to apply everywhere if its
//   neutral value is the old behaviour.
//
// And a rig MUST reset the forward on unmount. A yaw left over from
// the 3D view silently rotating input in the 2D view would be worse
// than a crash, because nothing would report it.
//
// ── THE VIEWS ───────────────────────────────────────────────────────
// A view supplies a distance MULTIPLIER, never a position. That is
// what lets one persisted setting be legal in a room, a plaza and a
// boardwalk at once: each keeps the framing it was designed with, and
// "Follow" means 50% closer than whatever THAT scene already thought
// was right.
//
// Persisted, because a camera choice must survive the screen and the
// reload or the player re-picks it at every door and stops bothering.
// The persisted id is VALIDATED against the live list on read, so
// dropping a view later self-heals instead of stranding somebody in a
// mode that no longer exists.
// ════════════════════════════════════════════════════════════════════

export const VIEWS = {
  follow: { id: "follow", label: "FOLLOW", mult: 0.5 },
  trail:  { id: "trail",  label: "TRAIL",  mult: 1.0 },
};
export const VIEW_ORDER = ["follow", "trail"];
export const DEFAULT_VIEW = "trail";

const LS_KEY = "ie.pier.camview";

// ── forward ─────────────────────────────────────────────────────────
// Tile space: +x is east, +y is SOUTH (screen-down), matching the map
// arrays. The identity is (0,-1) = north = screen-up.
const FWD = { x: 0, y: -1 };

export function setGroundForward(x, y) {
  const m = Math.hypot(x, y);
  if (!m || !isFinite(m)) return;
  FWD.x = x / m;
  FWD.y = y / m;
}
export function resetGroundForward() { FWD.x = 0; FWD.y = -1; }
export function groundForward() { return FWD; }

// ── THE ROTATION ────────────────────────────────────────────────────
// Input arrives as the raw key vector: ix = +1 for D/Right, iy = -1 for
// W/Up (screen-up), matching how the Pier has always read its keys.
// Out comes a world/tile-space direction.
//
//   W means "away from the camera". ALWAYS. That is the entire promise,
//   and it is why the player never walks south while the screen says
//   north once they have moved the camera.
//
// ⚠ ONLY DIRECTION IS CAMERA-RELATIVE. There is no speed term in this
// function and there must never be one — a camera is for looking; it
// does not get a vote on how fast you walk.
export function rotateInput(ix, iy) {
  if (!ix && !iy) return { x: 0, y: 0 };
  const f = FWD;
  const rx = -f.y, ry = f.x;          // right = forward turned 90° CW
  const fwdAmt = -iy;                 // W (iy=-1) → +1 forward
  let x = fwdAmt * f.x + ix * rx;
  let y = fwdAmt * f.y + ix * ry;
  const m = Math.hypot(x, y);         // normalise, or diagonals run √2 fast
  return m ? { x: x / m, y: y / m } : { x: 0, y: 0 };
}

// ── the persisted view ──────────────────────────────────────────────
let _view = null;
const listeners = new Set();

export function getView() {
  if (_view) return _view;
  let stored = null;
  try { stored = localStorage.getItem(LS_KEY); } catch { /* private mode */ }
  _view = VIEWS[stored] ? stored : DEFAULT_VIEW;   // validate on read
  return _view;
}
export function setView(id) {
  if (!VIEWS[id]) return;
  _view = id;
  try { localStorage.setItem(LS_KEY, id); } catch { /* private mode */ }
  bump();
}
export function viewMult() { return VIEWS[getView()].mult; }

// ── THE FREE POSE ───────────────────────────────────────────────────
// A view is a starting framing, not a cage. The instant the player
// drags, the rig stops steering and CARRIES the pose they chose — by
// TRANSLATING it with the character, never by re-aiming it. Re-aiming
// snaps a low eye-level shot back to over-the-shoulder and the player
// feels fought.
let _posed = false;
export function isPosed() { return _posed; }
export function setPosed(v) { if (_posed !== !!v) { _posed = !!v; bump(); } }

// ── DEFAULT IS AN ACTION, NOT A THIRD VIEW ──────────────────────────
// ⚠ A COUNTER, NOT A BOOLEAN. Pressing Default while already on Trail
// must still work; a boolean makes the button silently dead in exactly
// the most likely case.
let _resetCount = 0;
export function resetCamera() { _resetCount++; _posed = false; setView(DEFAULT_VIEW); bump(); }
export function resetToken() { return _resetCount; }

// Selecting a view must ALSO release the pose, or Follow appears to do
// nothing once the camera has been hand-placed, and reads as a bug.
export function chooseView(id) { _posed = false; setView(id); }

function bump() { for (const l of listeners) { try { l(); } catch {} } }
export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }

// ── THE RENDERER ────────────────────────────────────────────────────
// There is no renderer toggle. caP'n, 2026-08-25: "yes default and no
// toggle." The Pier IS 3D. get3D/set3D existed for about an hour and
// were removed rather than left dormant — a persisted preference for a
// decision nobody is allowed to make is just a stale key in everyone's
// localStorage.
